import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import Input from "@app/components/Input";
import { UserConnexionResponse } from "@api/src/types/responses";
import { useNavigate, useSearchParams } from "react-router";
import useUser from "@app/zustand/user";
import API from "@app/services/api";
import dayjs from "dayjs";
import { Trans, useLingui } from "@lingui/macro";
import AccessRequestForm from "@app/components/AccessRequestForm";
import PscLoginButton from "@app/components/PscLoginButton";
import CGUS from "./cgus";
import PrivacyPolicy from "./confidentialité";

/**
 * The website's "Subscribe" button links to `/welcome?acces=1`; `early=1` is what it linked to
 * while the platform was in its early users programme, and those links still circulate. Either
 * opens the access request dialog over the login form; anywhere else the dialog stays closed.
 */
function opensOnAccessRequest(searchParams: URLSearchParams): boolean {
  return ["acces", "early"].some((key) => {
    const v = searchParams.get(key)?.trim().toLowerCase();
    return v !== undefined && ["", "1", "true", "yes"].includes(v);
  });
}

interface PscStatusResponse {
  available?: boolean;
  environment?: string;
}

export default function WelcomeConnexion() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useLingui();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState(
    import.meta.env.VITE_EMAIL ||
    localStorage.getItem("mage-x-last-email") ||
    "",
  );
  const setUser = useUser((state) => state.setUser);

  // Whether the access request dialog is open lives in the URL, not in state: changing the language
  // remounts the page, and a state would fall back to whatever the URL said on arrival — the
  // access request, for a visitor who came from the website and has since closed it.
  const accessOpen = opensOnAccessRequest(searchParams);
  const setAccessOpen = (open: boolean) =>
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        params.delete("early");
        if (open) params.set("acces", "1");
        else params.delete("acces");
        return params;
      },
      { replace: true },
    );
  // An address typed at login that has no account carries over to the access request.
  const [signupEmail, setSignupEmail] = useState("");
  const [noAccount, setNoAccount] = useState(false);

  // Pro Santé Connect only shows up when the instance is actually wired up: the ANS
  // grants the authorisation and the client credentials come with it. While they are
  // missing, the button would lead straight to a failure.
  const [pscAvailable, setPscAvailable] = useState(false);
  const pscError = searchParams.get("psc_error");

  useEffect(() => {
    API.get({ path: "/user/psc/status" }).then((res: PscStatusResponse) => {
      setPscAvailable(Boolean(res?.available));
    });
  }, []);

  const handleLogin = (e: React.FormEvent<HTMLFormElement>) => {
    const username = (e.target as HTMLFormElement).username.value;
    e.preventDefault();
    setLoading(true);
    // Persist so the next visit opens with the address already typed
    localStorage.setItem("mage-x-last-email", email);
    API.post({
      path: "/user/login-redirect",
      body: { email, username },
    })
      .then((response: UserConnexionResponse) => {
        if (response.data?.mfa_required && response.data.mfa_token) {
          // Primary factor OK but app MFA is enabled — go to the TOTP screen.
          // Pass the short-lived token via navigation state (never in the URL).
          navigate(
            `/welcome/login-redirect?email=${encodeURIComponent(email)}`,
            { state: { mfaToken: response.data.mfa_token } },
          );
        } else if (response.message === "psc_required") {
          // An account bound to an e-CPS and holding no password: no code was sent,
          // the way in is Pro Santé Connect.
          navigate(
            `/welcome/login-redirect?email=${encodeURIComponent(email)}&step=psc`,
          );
        } else if (response.message === "password_required") {
          // Le compte a un mot de passe : c'est lui le premier facteur, aucun
          // code n'a été envoyé par email. L'étape passe par l'URL (et non par
          // l'état de navigation) pour survivre à un rafraîchissement de page.
          navigate(
            `/welcome/login-redirect?email=${encodeURIComponent(email)}&step=password`,
          );
        } else if (
          response.message === "setup_required" &&
          response.data?.user
        ) {
          // The session handed over only serves the compliance wizard.
          // RestrictedRoute renders it in place of the application.
          setUser(response.data.user);
          navigate("/");
        } else if (response.message === "logged_in" && response.data?.user) {
          // Localhost / dev bypass: the backend already minted the session
          // cookie (no email code needed). Go straight in.
          setUser(response.data.user);
          navigate("/");
        } else if (response.message === "Redirect to OTP" && response.data) {
          navigate(
            `/welcome/login-redirect?email=${encodeURIComponent(response.data.user?.email || "")}`,
          );
        } else if (response.message === "no_account") {
          setSignupEmail(email);
          setNoAccount(true);
          setAccessOpen(true);
        } else if (
          response.message === "Redirect to Login Profile" &&
          response.data
        ) {
          setUser(response.data.user);
          navigate("/welcome/login-profile");
        } else if (response.error) {
          alert(response.error);
        } else if (response.ok) {
          navigate("/");
        }
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="flex flex-col">
      {/* Logo centered above the login form */}
      <div className="flex justify-center mb-8">
        <img
          src="/logo.png"
          alt="MAGE-X"
          className="h-10 [filter:brightness(0)_saturate(100%)_invert(0%)_sepia(95%)_saturate(5549%)_hue-rotate(235deg)_brightness(6%)_contrast(108%)]"
        />
      </div>

      {/* Pro Santé Connect is the nominal means for RPPS holders, so it comes first —
          and it needs no e-mail address, which is why it sits outside the form rather
          than under it. */}
      {pscAvailable && (
        <div className="mb-6">
          <PscLoginButton />
          {pscError && (
            <p className="mt-2 text-center text-xs text-red-600">
              {pscError === "unknown_practitioner" ? (
                <Trans>
                  Votre carte n'est encore rattachée à aucun compte MAGE-X.
                  Connectez-vous avec votre email, puis associez-la depuis
                  Configuration → Compte. Pas encore de compte ? Inscrivez-vous
                  ci-dessous.
                </Trans>
              ) : pscError === "cancelled" ? (
                <Trans>Connexion annulée.</Trans>
              ) : (
                <Trans>
                  La connexion par Pro Santé Connect a échoué. Utilisez votre
                  email et votre mot de passe, ou réessayez.
                </Trans>
              )}
            </p>
          )}
          <div className="mt-6 flex items-center gap-3">
            <span className="h-px flex-1 bg-slate-200" />
            <span className="text-xs uppercase tracking-wide text-slate-400">
              <Trans>ou</Trans>
            </span>
            <span className="h-px flex-1 bg-slate-200" />
          </div>
        </div>
      )}

      <form
        action="/welcome/login-redirect"
        method="POST"
        className="flex flex-col gap-4"
        autoComplete="off"
        encType="application/x-www-form-urlencoded"
        onSubmit={handleLogin}
      >
        <Input
          label={t`Votre email`}
          name="email"
          type="email"
          autoComplete="email"
          inputMode="email"
          placeholder="doc@mage-x.fr"
          required
          customValidity={t`Veuillez entrer un email valide`}
          value={email}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setEmail(e.target.value)
          }
        />
        {/* Honeypot */}
        <input
          type="text"
          name="username"
          placeholder="Veuillez entrer un nom d'utilisateur"
          className="hidden"
        />
        <button
          className="w-full rounded-lg bg-gradient-to-r from-primary to-primary/90 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:shadow-md transition-all disabled:cursor-not-allowed disabled:opacity-50"
          type="submit"
          disabled={loading}
        >
          {loading ? <Trans>Connexion...</Trans> : <Trans>Se connecter</Trans>}
        </button>
        <p className="text-center text-xs text-slate-400">
          <Trans>
            Vous saisirez ensuite votre mot de passe, ou un code de vérification
            envoyé par email
          </Trans>
        </p>
      </form>

      {/* No account yet: the access request opens in a dialog rather than beside the login. */}
      <div className="mt-6 border-t border-slate-200 pt-6 text-center">
        <p className="mb-3 text-sm text-slate-500">
          <Trans>Pas encore de compte ?</Trans>
        </p>
        <button
          type="button"
          onClick={() => setAccessOpen(true)}
          className="w-full rounded-lg border border-primary px-6 py-2.5 text-sm font-semibold text-primary transition-colors hover:bg-primary/5"
        >
          <Trans>S'inscrire</Trans>
        </button>
      </div>

      <Dialog.Root
        open={accessOpen}
        onOpenChange={(open) => {
          if (!open) setNoAccount(false);
          setAccessOpen(open);
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
          {/* A click beside the dialog does not close it: it would drop a half-filled form. */}
          <Dialog.Content
            onInteractOutside={(e) => e.preventDefault()}
            className="fixed left-1/2 top-1/2 z-50 flex max-h-[90vh] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl bg-white shadow-2xl focus:outline-none"
          >
            <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-4">
              <div>
                <Dialog.Title className="text-base font-semibold text-slate-900">
                  <Trans>Rejoindre MAGE-X</Trans>
                </Dialog.Title>
                <Dialog.Description className="mt-1 text-sm text-slate-600">
                  <Trans>
                    Médecins, hôpitaux, sociétés savantes et industriels de
                    santé : l'équipe MAGE-X examine chaque demande d'accès.
                  </Trans>
                </Dialog.Description>
              </div>
              <Dialog.Close
                aria-label={t`Fermer`}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </Dialog.Close>
            </div>
            <div className="overflow-y-auto px-6 py-5">
              {noAccount && (
                <p className="mb-4 rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-900">
                  <Trans>
                    Aucun compte n'est associé à cet email. Faites une demande
                    d'accès, l'équipe MAGE-X vous répondra rapidement.
                  </Trans>
                </p>
              )}
              <AccessRequestForm
                key={signupEmail}
                initialEmail={signupEmail}
                onAccountExists={(existing) => {
                  setEmail(existing);
                  setNoAccount(false);
                  setAccessOpen(false);
                }}
              />
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Legal notice */}
      <LegalNotice />

      <footer className="mt-6 text-center text-xs text-slate-400">
        <p>Copyright MAGE-X {dayjs().year()} — version 1.1.0</p>
      </footer>
    </div>
  );
}

function LegalNotice() {
  const [open, setOpen] = useState<"cgu" | "privacy" | null>(null);

  return (
    <>
      <p className="mt-4 text-center text-xs text-slate-400 leading-relaxed">
        <Trans>En continuant, vous acceptez nos</Trans>{" "}
        <button
          type="button"
          onClick={() => setOpen("cgu")}
          className="underline underline-offset-2 hover:text-primary transition-colors"
        >
          <Trans>Conditions d'utilisation</Trans>
        </button>{" "}
        <Trans>et notre</Trans>{" "}
        <button
          type="button"
          onClick={() => setOpen("privacy")}
          className="underline underline-offset-2 hover:text-primary transition-colors"
        >
          <Trans>Politique de confidentialité</Trans>
        </button>
        .
      </p>

      <Dialog.Root
        open={open !== null}
        onOpenChange={(v) => !v && setOpen(null)}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[85vh] w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl bg-white shadow-2xl focus:outline-none">
            <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
              <Dialog.Title className="text-base font-semibold text-slate-900">
                {open === "cgu" ? (
                  <Trans>Conditions d'utilisation</Trans>
                ) : (
                  <Trans>Politique de confidentialité</Trans>
                )}
              </Dialog.Title>
              <Dialog.Close className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </Dialog.Close>
            </div>
            <div className="overflow-y-auto max-h-[calc(85vh-4rem)] px-2 prose prose-sm prose-slate max-w-none">
              {open === "cgu" ? <CGUS /> : <PrivacyPolicy />}
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}
